import { useState, useCallback, useEffect, useRef } from 'react';
import { useTermometroEmissor } from '../hooks/useTermometro';
import type { TipoVoto, TimeVoto } from '../hooks/useTermometro';

interface Props {
  telaId:           string;
  fixtureId:        number;
  nomeCasa?:        string;
  nomeVisitante?:   string;
  escudoCasa?:      string | null;
  escudoVisitante?: string | null;
}

interface Ripple {
  id:   number;
  tipo: TipoVoto;
}

let rippleCounter = 0;

export default function MobileTermometro({
  telaId, fixtureId, nomeCasa, nomeVisitante, escudoCasa, escudoVisitante,
}: Props) {
  const { enviarClique } = useTermometroEmissor(telaId, fixtureId);

  const [timeEscolhido, setTimeEscolhido] = useState<TimeVoto>('casa');
  const [totalApoiar,   setTotalApoiar]   = useState(0);
  const [totalSecar,    setTotalSecar]    = useState(0);
  const [ripples,       setRipples]       = useState<Ripple[]>([]);
  const [pressed,       setPressed]       = useState<TipoVoto | null>(null);
  const [timePressed,   setTimePressed]   = useState<TimeVoto | null>(null);
  const [erroEscudo,    setErroEscudo]    = useState({ casa: false, visitante: false });

  // Rastreia os timeouts dos ripples para limpá-los no unmount (evita state update
  // após desmontar quando o cliente clica muito).
  const rippleTimeoutsRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());
  useEffect(() => {
    const timeouts = rippleTimeoutsRef.current;
    return () => { timeouts.forEach(clearTimeout); timeouts.clear(); };
  }, []);

  const adicionarRipple = useCallback((tipo: TipoVoto) => {
    const id = ++rippleCounter;
    setRipples(prev => [...prev, { id, tipo }]);
    const t = setTimeout(() => {
      rippleTimeoutsRef.current.delete(t);
      setRipples(prev => prev.filter(r => r.id !== id));
    }, 600);
    rippleTimeoutsRef.current.add(t);
  }, []);

  const handleApoiar = useCallback(() => {
    void enviarClique('apoiar', timeEscolhido).then(resultado => {
      if (resultado !== 'ok') return;
      setTotalApoiar(n => n + 1);
      adicionarRipple('apoiar');
    }).catch(() => {});
  }, [enviarClique, timeEscolhido, adicionarRipple]);

  const handleSecar = useCallback(() => {
    void enviarClique('secar', timeEscolhido).then(resultado => {
      if (resultado !== 'ok') return;
      setTotalSecar(n => n + 1);
      adicionarRipple('secar');
    }).catch(() => {});
  }, [enviarClique, timeEscolhido, adicionarRipple]);

  const total     = totalApoiar + totalSecar;
  const pctApoiar = total > 0 ? Math.round((totalApoiar / total) * 100) : 50;
  const pctSecar  = 100 - pctApoiar;

  const nomeTime = timeEscolhido === 'casa' ? (nomeCasa ?? 'Casa') : (nomeVisitante ?? 'Visitante');

  return (
    <div className="flex select-none flex-col overflow-hidden rounded-2xl border border-white/[0.06] bg-white/[0.02]">

      {/* ── Cabeçalho ── */}
      <div className="flex shrink-0 items-center justify-between px-4 pb-2 pt-3">
        <p className="text-[11px] font-medium text-white/35">
          Termômetro
        </p>
        <p className="max-w-[55%] truncate text-right text-[11px] font-medium text-white/45">
          {nomeCasa && nomeVisitante ? `${nomeCasa} × ${nomeVisitante}` : 'No ar'}
        </p>
      </div>

      {/* ── Seletor de time ── */}
      <div className="flex gap-2 mx-4 mb-2 shrink-0">
        {(['casa', 'visitante'] as TimeVoto[]).map(time => {
          const nome     = time === 'casa' ? (nomeCasa ?? 'Casa') : (nomeVisitante ?? 'Visitante');
          const escudo   = time === 'casa' ? escudoCasa : escudoVisitante;
          const ativo    = timeEscolhido === time;
          const escudoOk = !!escudo && !erroEscudo[time];
          return (
            <button
              key={time}
              onPointerDown={() => { setTimePressed(time); setTimeEscolhido(time); }}
              onPointerUp={() => setTimePressed(null)}
              onPointerCancel={() => setTimePressed(null)}
              onPointerLeave={() => setTimePressed(null)}
              className={`
                flex-1 py-1.5 rounded-xl text-xs font-bold transition-all duration-150 px-2
                ${ativo
                  ? 'bg-white/15 text-white border border-white/25'
                  : 'bg-white/[0.04] text-white/35 border border-white/[0.06]'}
                ${timePressed === time ? 'scale-95' : ativo ? 'scale-[1.02]' : 'scale-100'}
              `}
            >
              <div className="flex items-center justify-center gap-1.5 min-w-0">
                {escudoOk && (
                  <img
                    src={escudo!}
                    alt=""
                    className="w-4 h-4 object-contain shrink-0"
                    onError={() => setErroEscudo(prev => ({ ...prev, [time]: true }))}
                  />
                )}
                <span className="truncate">{nome}</span>
              </div>
            </button>
          );
        })}
      </div>

      {/* ── Cabo de guerra ── */}
      <div className="mx-4 mb-3 shrink-0">
        <div className="flex h-2.5 rounded-full overflow-hidden">
          <div
            className="bg-gradient-to-r from-emerald-500 to-green-400 transition-all duration-500"
            style={{ width: `${pctApoiar}%` }}
          />
          <div className="bg-gradient-to-l from-rose-500 to-orange-400 flex-1 transition-all duration-500" />
        </div>
        <div className="flex justify-between mt-1">
          <span className="text-[10px] font-bold text-emerald-400">{pctApoiar}%</span>
          <span className="text-[10px] font-bold text-rose-400">{pctSecar}%</span>
        </div>
      </div>

      {/* ── Botões APOIAR / SECAR ── */}
      <div className="flex gap-3 px-4 pb-4 min-h-[150px]">

        {/* APOIAR */}
        <button
          onPointerDown={() => { setPressed('apoiar'); handleApoiar(); }}
          onPointerUp={() => setPressed(null)}
          onPointerCancel={() => setPressed(null)}
          onPointerLeave={() => setPressed(null)}
          className={`relative flex-1 rounded-2xl overflow-hidden transition-transform duration-100 ${pressed === 'apoiar' ? 'scale-[0.96]' : ''}`}
          style={{
            background:  'linear-gradient(160deg, #065f46 0%, #0369a1 100%)',
            boxShadow:   '0 6px 24px rgba(5,150,105,0.4), inset 0 1px 0 rgba(255,255,255,0.12)',
            touchAction: 'manipulation',
          }}
        >
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 pointer-events-none">
            <span style={{ fontSize: 44, lineHeight: 1, filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.5))' }}>🚀</span>
            <span className="text-white font-black text-lg tracking-wide drop-shadow-md">APOIAR</span>
            <span className="text-white/60 text-[10px] font-semibold uppercase tracking-wider px-2 text-center leading-tight">
              {nomeTime}
            </span>
            {totalApoiar > 0 && (
              <span className="mt-1 px-2 py-0.5 rounded-full bg-white/15 text-white/80 text-[10px] font-bold">
                {totalApoiar}×
              </span>
            )}
          </div>
          {ripples.filter(r => r.tipo === 'apoiar').map(r => (
            <span
              key={r.id}
              className="absolute inset-0 m-auto w-[60px] h-[60px] rounded-full pointer-events-none bg-white/30 animate-termometro-ripple"
            />
          ))}
        </button>

        {/* SECAR */}
        <button
          onPointerDown={() => { setPressed('secar'); handleSecar(); }}
          onPointerUp={() => setPressed(null)}
          onPointerCancel={() => setPressed(null)}
          onPointerLeave={() => setPressed(null)}
          className={`relative flex-1 rounded-2xl overflow-hidden transition-transform duration-100 ${pressed === 'secar' ? 'scale-[0.96]' : ''}`}
          style={{
            background:  'linear-gradient(160deg, #7f1d1d 0%, #9a3412 100%)',
            boxShadow:   '0 6px 24px rgba(220,38,38,0.4), inset 0 1px 0 rgba(255,255,255,0.12)',
            touchAction: 'manipulation',
          }}
        >
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 pointer-events-none">
            <span style={{ fontSize: 44, lineHeight: 1, filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.5))' }}>🤬</span>
            <span className="text-white font-black text-lg tracking-wide drop-shadow-md">SECAR</span>
            <span className="text-white/60 text-[10px] font-semibold uppercase tracking-wider px-2 text-center leading-tight">
              {nomeTime}
            </span>
            {totalSecar > 0 && (
              <span className="mt-1 px-2 py-0.5 rounded-full bg-white/15 text-white/80 text-[10px] font-bold">
                {totalSecar}×
              </span>
            )}
          </div>
          {ripples.filter(r => r.tipo === 'secar').map(r => (
            <span
              key={r.id}
              className="absolute inset-0 m-auto w-[60px] h-[60px] rounded-full pointer-events-none bg-white/30 animate-termometro-ripple"
            />
          ))}
        </button>

      </div>
    </div>
  );
}
